import React, { useState, useEffect, useCallback } from 'react';
import { useStore } from '../../context/StoreContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../../components/ui/table';
import {
  Package, ArrowUp, ArrowDown, Barcode, QrCode, 
  MagnifyingGlass, CircleNotch, FloppyDisk, Download,
  ClockCounterClockwise, WarningCircle, Check
} from '@phosphor-icons/react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const StockPage = () => {
  const { currentStore } = useStore();
  const [products, setProducts] = useState([]);
  const [movements, setMovements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [tab, setTab] = useState('stock'); // stock, movements
  const [changes, setChanges] = useState({}); // {product_id: quantity_to_add}
  const [saving, setSaving] = useState(false);
  const [barcodeLoading, setBarcodeLoading] = useState({});

  const fetchStock = useCallback(async () => {
    if (!currentStore) return;
    try {
      const res = await axios.get(`${API_URL}/api/stores/${currentStore.id}/stock`, { withCredentials: true });
      setProducts(res.data);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  }, [currentStore]);

  const fetchMovements = useCallback(async () => {
    if (!currentStore) return;
    try {
      const res = await axios.get(`${API_URL}/api/stores/${currentStore.id}/stock/movements`, { withCredentials: true });
      setMovements(res.data);
    } catch (e) { console.error(e); }
  }, [currentStore]);

  useEffect(() => { fetchStock(); }, [fetchStock]);
  useEffect(() => { if (tab === 'movements') fetchMovements(); }, [tab, fetchMovements]);

  const handleQuantityChange = (productId, value) => {
    const num = parseInt(value) || 0;
    setChanges(prev => {
      const next = { ...prev };
      if (num === 0) delete next[productId];
      else next[productId] = num;
      return next;
    });
  };

  const handleSaveBulk = async () => {
    if (Object.keys(changes).length === 0) return;
    setSaving(true);
    try {
      const items = Object.entries(changes).map(([product_id, quantity]) => ({
        product_id,
        quantity,
        reason: quantity > 0 ? 'manual_entry' : 'manual_exit'
      }));
      await axios.post(`${API_URL}/api/stores/${currentStore.id}/stock/bulk-update`, { items }, { withCredentials: true });
      toast.success(`${items.length} produit(s) mis à jour`);
      setChanges({});
      fetchStock();
    } catch (e) {
      toast.error('Erreur lors de la mise à jour');
    } finally { setSaving(false); }
  };

  const handleGenerateBarcode = async (productId) => {
    setBarcodeLoading(prev => ({ ...prev, [productId]: 'barcode' }));
    try {
      const res = await axios.get(`${API_URL}/api/stores/${currentStore.id}/products/${productId}/barcode`, { withCredentials: true });
      const link = document.createElement('a');
      link.href = `${API_URL}${res.data.url}`;
      link.download = `barcode_${res.data.product_name}.png`;
      link.target = '_blank';
      link.click();
      toast.success('Code-barres généré');
    } catch (e) {
      toast.error('Erreur génération code-barres');
    } finally { setBarcodeLoading(prev => ({ ...prev, [productId]: null })); }
  };

  const handleGenerateQR = async (productId) => {
    setBarcodeLoading(prev => ({ ...prev, [productId]: 'qr' }));
    try {
      const res = await axios.get(`${API_URL}/api/stores/${currentStore.id}/products/${productId}/qrcode`, { withCredentials: true });
      const link = document.createElement('a');
      link.href = `${API_URL}${res.data.url}`;
      link.download = `qrcode_${res.data.product_name}.png`;
      link.target = '_blank';
      link.click();
      toast.success('QR code généré');
    } catch (e) {
      toast.error('Erreur génération QR code');
    } finally { setBarcodeLoading(prev => ({ ...prev, [productId]: null })); }
  };

  const filtered = products.filter(p =>
    p.name?.toLowerCase().includes(search.toLowerCase()) ||
    p.sku?.toLowerCase().includes(search.toLowerCase())
  );

  const lowStock = products.filter(p => p.manage_stock && p.stock_quantity <= 5);
  const totalValue = products.reduce((sum, p) => sum + (p.stock_quantity || 0) * (p.price || 0), 0);

  if (!currentStore) return <div className="text-center py-12 text-slate-500">Sélectionnez une boutique</div>;

  return (
    <div className="space-y-6" data-testid="stock-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Gestion de stock</h1>
          <p className="text-sm text-slate-500 mt-1">Entrées, sorties, codes-barres et QR codes</p>
        </div>
        {Object.keys(changes).length > 0 && (
          <Button onClick={handleSaveBulk} disabled={saving} className="bg-[#0052FF] hover:bg-[#0040CC]" data-testid="save-stock-btn">
            {saving ? <CircleNotch className="animate-spin mr-2" size={16} /> : <FloppyDisk className="mr-2" size={16} />}
            Enregistrer ({Object.keys(changes).length})
          </Button>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Package size={20} className="text-blue-600" />
            </div>
            <div>
              <p className="text-xs text-slate-500">Références</p>
              <p className="text-lg font-bold">{products.length}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
              <ArrowUp size={20} className="text-emerald-600" />
            </div>
            <div>
              <p className="text-xs text-slate-500">Unités totales</p>
              <p className="text-lg font-bold">{products.reduce((s, p) => s + (p.stock_quantity || 0), 0)}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
              <WarningCircle size={20} className="text-amber-600" />
            </div>
            <div>
              <p className="text-xs text-slate-500">Stock faible</p>
              <p className="text-lg font-bold">{lowStock.length}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 bg-violet-100 rounded-lg flex items-center justify-center">
              <Barcode size={20} className="text-violet-600" />
            </div>
            <div>
              <p className="text-xs text-slate-500">Valeur stock</p>
              <p className="text-lg font-bold">{totalValue.toFixed(2)} EUR</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b">
        <button onClick={() => setTab('stock')} className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${tab === 'stock' ? 'border-[#0052FF] text-[#0052FF]' : 'border-transparent text-slate-500 hover:text-slate-700'}`} data-testid="tab-stock">
          Entrée / Sortie de stock
        </button>
        <button onClick={() => setTab('movements')} className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${tab === 'movements' ? 'border-[#0052FF] text-[#0052FF]' : 'border-transparent text-slate-500 hover:text-slate-700'}`} data-testid="tab-movements">
          <ClockCounterClockwise size={14} className="inline mr-1" />Historique mouvements
        </button>
      </div>

      {tab === 'stock' ? (
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center gap-3">
              <div className="relative flex-1 max-w-sm">
                <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Rechercher par nom ou SKU..."
                  className="pl-9"
                  data-testid="stock-search"
                />
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 text-center"><CircleNotch className="animate-spin mx-auto" size={24} /></div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Produit</TableHead>
                    <TableHead>SKU</TableHead>
                    <TableHead className="text-center">Stock actuel</TableHead>
                    <TableHead className="text-center">Entrée (+) / Sortie (-)</TableHead>
                    <TableHead className="text-center">Nouveau stock</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map(p => {
                    const change = changes[p.id] || 0;
                    const newQty = (p.stock_quantity || 0) + change;
                    return (
                      <TableRow key={p.id} className={newQty < 0 ? 'bg-red-50' : change !== 0 ? 'bg-blue-50/50' : ''}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {(p.featured_image || p.images?.[0]) ? (
                              <img src={p.featured_image || p.images[0]} alt="" className="w-8 h-8 rounded object-cover" />
                            ) : (
                              <div className="w-8 h-8 bg-slate-100 rounded flex items-center justify-center">
                                <Package size={14} className="text-slate-400" />
                              </div>
                            )}
                            <span className="font-medium text-sm">{p.name}</span>
                          </div>
                        </TableCell>
                        <TableCell><code className="text-xs bg-slate-100 px-1.5 py-0.5 rounded">{p.sku || '-'}</code></TableCell>
                        <TableCell className="text-center">
                          <Badge variant={p.stock_quantity <= 5 ? 'destructive' : p.stock_quantity <= 20 ? 'secondary' : 'default'}>
                            {p.stock_quantity ?? 0}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-center">
                          <Input
                            type="number"
                            value={change || ''}
                            onChange={e => handleQuantityChange(p.id, e.target.value)}
                            className="w-24 mx-auto text-center"
                            placeholder="0"
                            data-testid={`stock-input-${p.id}`}
                          />
                        </TableCell>
                        <TableCell className="text-center font-semibold">
                          {change !== 0 ? (
                            <span className={newQty < 0 ? 'text-red-600' : 'text-emerald-600'}>{newQty}</span>
                          ) : '-'}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleGenerateBarcode(p.id)}
                              disabled={barcodeLoading[p.id] === 'barcode'}
                              title="Télécharger code-barres"
                              data-testid={`barcode-btn-${p.id}`}
                            >
                              {barcodeLoading[p.id] === 'barcode' ? <CircleNotch className="animate-spin" size={16} /> : <Barcode size={16} />}
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleGenerateQR(p.id)}
                              disabled={barcodeLoading[p.id] === 'qr'}
                              title="Télécharger QR code"
                              data-testid={`qr-btn-${p.id}`}
                            >
                              {barcodeLoading[p.id] === 'qr' ? <CircleNotch className="animate-spin" size={16} /> : <QrCode size={16} />}
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            {movements.length === 0 ? (
              <div className="p-8 text-center text-slate-500">Aucun mouvement de stock enregistré</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Produit</TableHead>
                    <TableHead className="text-center">Quantité</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Référence</TableHead>
                    <TableHead>Par</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {movements.map(m => (
                    <TableRow key={m.id}>
                      <TableCell className="text-sm">{new Date(m.created_at).toLocaleString('fr-FR')}</TableCell>
                      <TableCell className="text-sm font-medium">{products.find(p => p.id === m.product_id)?.name || m.product_id}</TableCell>
                      <TableCell className="text-center">
                        <span className={`font-semibold ${m.quantity > 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                          {m.quantity > 0 ? '+' : ''}{m.quantity}
                        </span>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs">
                          {m.reason === 'manual_entry' ? 'Entrée manuelle' :
                           m.reason === 'manual_exit' ? 'Sortie manuelle' :
                           m.reason === 'purchase' ? 'Vente' :
                           m.reason === 'return' ? 'Retour' : m.reason}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-slate-500">{m.reference || '-'}</TableCell>
                      <TableCell className="text-sm text-slate-500">{m.user_name || '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default StockPage;
